/**
 * Domein-constanten voor het Human Design Mandala-wiel.
 *
 * Eén bron van waarheid: de **poortvolgorde tegen de klok in** (CCW, in
 * toenemende zodiac-lengte) plus het **graden-anker** (poort 41 start op
 * Waterman 2°00′00″ = 302° absolute lengte). Hieruit zijn de dierenriem-ring
 * (12 × 30°) en de kwartaal-ring (4 × 90°) volledig afleidbaar.
 */

export const MANDALA_GATE_COUNT = 64;
export const MANDALA_LINES_PER_GATE = 6;
export const MANDALA_DEGREES_PER_GATE = 360 / MANDALA_GATE_COUNT; // 5.625
export const MANDALA_DEGREES_PER_LINE = MANDALA_DEGREES_PER_GATE / MANDALA_LINES_PER_GATE; // 0.9375

/** Poort 41 start = Waterman 2°00′00″ = 302° absolute zodiac-lengte. */
export const MANDALA_GATE_41_START_LONGITUDE = 302;

/**
 * Poortvolgorde **tegen de klok in** (CCW), in toenemende zodiac-lengte,
 * startend bij Q1 / poort 13. Index → poortnummer. Elke index stapt
 * {@link MANDALA_DEGREES_PER_GATE} verder in lengte. De volgorde is cyclisch
 * en is door de gebruiker bevestigd.
 */
export const MANDALA_GATE_SEQUENCE_CCW: readonly number[] = [
  // Q1 — Initiatie (13 t/m 24)
  13, 49, 30, 55, 37, 63, 22, 36, 25, 17, 21, 51, 42, 3, 27, 24,
  // Q2 — Beschaving (2 t/m 33)
  2, 23, 8, 20, 16, 35, 45, 12, 15, 52, 39, 53, 62, 56, 31, 33,
  // Q3 — Dualiteit (7 t/m 44)
  7, 4, 29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50, 28, 44,
  // Q4 — Mutatie (1 t/m 19)
  1, 43, 14, 34, 9, 5, 26, 11, 10, 58, 38, 54, 61, 60, 41, 19,
];

export interface MandalaQuarterDefinition {
  readonly key: string;
  readonly label: string;
  /** Keynote-deel vóór de ster: "Purpose through …" (Rave Mandala-terminologie, bewust Engels). */
  readonly purpose: string;
  readonly star: string;
  /** Engelse kwartaaltitel zoals op de klassieke Rave Mandala ("Quarter of …"). */
  readonly title: string;
  /** Index in {@link MANDALA_GATE_SEQUENCE_CCW} waar dit kwartaal start (16 poorten breed). */
  readonly sequenceStartIndex: number;
}

export const MANDALA_QUARTERS: readonly MandalaQuarterDefinition[] = [
  {
    key: 'initiation',
    label: 'Kwartaal van Initiatie',
    purpose: 'Purpose through Mind',
    star: 'Alcyone',
    title: 'Quarter of Initiation',
    sequenceStartIndex: 0,
  },
  {
    key: 'civilization',
    label: 'Kwartaal van Beschaving',
    purpose: 'Purpose through Form',
    star: 'Dubhe',
    title: 'Quarter of Civilization',
    sequenceStartIndex: 16,
  },
  {
    key: 'duality',
    label: 'Kwartaal van Dualiteit',
    purpose: 'Purpose through Bonding',
    star: 'Jupiter',
    title: 'Quarter of Duality',
    sequenceStartIndex: 32,
  },
  {
    key: 'mutation',
    label: 'Kwartaal van Mutatie',
    purpose: 'Purpose through Transformation',
    star: 'Sirius',
    title: 'Quarter of Mutation',
    sequenceStartIndex: 48,
  },
];

export const MANDALA_QUARTER_GATE_SPAN = MANDALA_GATE_COUNT / MANDALA_QUARTERS.length; // 16

export interface MandalaGodheadDefinition {
  readonly key: string;
  /** Naam zoals op "The Faces of the Godhead" (HDC-poster); taal-neutraal. */
  readonly name: string;
  /** Kwartaal waartoe deze godhead behoort (kleurfamilie van het segment). */
  readonly quarterKey: string;
  /** Index in {@link MANDALA_GATE_SEQUENCE_CCW} waar deze godhead start (4 poorten breed). */
  readonly sequenceStartIndex: number;
}

/**
 * De 16 godheads (4 per kwartaal, elk 4 opeenvolgende poorten in de
 * CCW-volgorde), conform de HDC-poster "The Faces of the Godhead"
 * (aangeleverd 2026-08-20). De poortentoewijzing volgt rechtstreeks uit de
 * kwartaal-indeling: Kali opent Initiatie (13-49-30-55), Maia Beschaving
 * (2-23-8-20), Thoth Dualiteit (7-4-29-59), Hades Mutatie (1-43-14-34).
 */
export const MANDALA_GODHEADS: readonly MandalaGodheadDefinition[] = [
  { key: 'kali', name: 'Kali', quarterKey: 'initiation', sequenceStartIndex: 0 },
  { key: 'mitra', name: 'Mitra', quarterKey: 'initiation', sequenceStartIndex: 4 },
  { key: 'michael', name: 'Michael', quarterKey: 'initiation', sequenceStartIndex: 8 },
  { key: 'janus', name: 'Janus', quarterKey: 'initiation', sequenceStartIndex: 12 },
  { key: 'maia', name: 'Maia', quarterKey: 'civilization', sequenceStartIndex: 16 },
  { key: 'lakshmi', name: 'Lakshmi', quarterKey: 'civilization', sequenceStartIndex: 20 },
  { key: 'parvati', name: 'Parvati', quarterKey: 'civilization', sequenceStartIndex: 24 },
  { key: 'maat', name: 'Maat', quarterKey: 'civilization', sequenceStartIndex: 28 },
  { key: 'thoth', name: 'Thoth', quarterKey: 'duality', sequenceStartIndex: 32 },
  { key: 'harmonia', name: 'Harmonia', quarterKey: 'duality', sequenceStartIndex: 36 },
  { key: 'christ', name: 'Christ', quarterKey: 'duality', sequenceStartIndex: 40 },
  { key: 'minerva', name: 'Minerva', quarterKey: 'duality', sequenceStartIndex: 44 },
  { key: 'hades', name: 'Hades', quarterKey: 'mutation', sequenceStartIndex: 48 },
  { key: 'prometheus', name: 'Prometheus', quarterKey: 'mutation', sequenceStartIndex: 52 },
  { key: 'vishnu', name: 'Vishnu', quarterKey: 'mutation', sequenceStartIndex: 56 },
  { key: 'keepers', name: 'Keepers of the Wheel', quarterKey: 'mutation', sequenceStartIndex: 60 },
];

export const MANDALA_GODHEAD_GATE_SPAN = MANDALA_GATE_COUNT / MANDALA_GODHEADS.length; // 4

export interface MandalaZodiacSignDefinition {
  readonly key: string;
  readonly name: string;
  /** Astronomicon-font-glyph (A = Ram t/m L = Vissen). */
  readonly glyph: string;
  /** Absolute lengte (graden) waar het teken start; veelvoud van 30. */
  readonly startLongitude: number;
}

export const MANDALA_ZODIAC_SIGNS: readonly MandalaZodiacSignDefinition[] = [
  { key: 'aries', name: 'Ram', glyph: 'A', startLongitude: 0 },
  { key: 'taurus', name: 'Stier', glyph: 'B', startLongitude: 30 },
  { key: 'gemini', name: 'Tweelingen', glyph: 'C', startLongitude: 60 },
  { key: 'cancer', name: 'Kreeft', glyph: 'D', startLongitude: 90 },
  { key: 'leo', name: 'Leeuw', glyph: 'E', startLongitude: 120 },
  { key: 'virgo', name: 'Maagd', glyph: 'F', startLongitude: 150 },
  { key: 'libra', name: 'Weegschaal', glyph: 'G', startLongitude: 180 },
  { key: 'scorpio', name: 'Schorpioen', glyph: 'H', startLongitude: 210 },
  { key: 'sagittarius', name: 'Boogschutter', glyph: 'I', startLongitude: 240 },
  { key: 'capricorn', name: 'Steenbok', glyph: 'J', startLongitude: 270 },
  { key: 'aquarius', name: 'Waterman', glyph: 'K', startLongitude: 300 },
  { key: 'pisces', name: 'Vissen', glyph: 'L', startLongitude: 330 },
];

export const MANDALA_DEGREES_PER_ZODIAC_SIGN = 360 / MANDALA_ZODIAC_SIGNS.length; // 30

export interface MandalaRingRadii {
  readonly innerRadius: number;
  readonly outerRadius: number;
}

/** Ringen staan hier in tekenvolgorde van buiten naar binnen. */
export interface MandalaGeometryConfig {
  readonly center: { readonly x: number; readonly y: number };
  readonly viewBoxSize: number;
  readonly quarterRing: MandalaRingRadii;
  readonly ichingRing: MandalaRingRadii;
  readonly gateRing: MandalaRingRadii;
  readonly lineRing: MandalaRingRadii;
  /**
   * Godhead-band: symbolisch BINNEN de Ring Pass Not (het wiel zelf), dus
   * tussen de poortenring en de dierenriem — even dik als de dierenriem.
   */
  readonly godheadRing: MandalaRingRadii;
  readonly zodiacRing: MandalaRingRadii;
}

/**
 * Voorlopige radii (placeholders voor Fase 3 visuele afstemming). De layout is
 * parametrisch: enkel deze config hoeft te wijzigen om de ring-afmetingen aan
 * te passen. ViewBox = 1000×1000, middelpunt (500, 500).
 *
 * Ringvolgorde van buiten naar binnen: kwartaal → I-Tjing → poorten/lijnen →
 * **dierenriem**. De dierenriem ligt bewust ín het wiel (tussen de poortenring
 * en het binnengat), zoals op de klassieke Rave Mandala.
 */
export const DEFAULT_MANDALA_GEOMETRY: MandalaGeometryConfig = {
  center: { x: 500, y: 500 },
  viewBoxSize: 1000,
  quarterRing: { innerRadius: 455, outerRadius: 498 },
  ichingRing: { innerRadius: 414, outerRadius: 446 },
  gateRing: { innerRadius: 366, outerRadius: 408 },
  lineRing: { innerRadius: 396, outerRadius: 408 },
  godheadRing: { innerRadius: 334, outerRadius: 360 },
  zodiacRing: { innerRadius: 298, outerRadius: 328 },
};

/**
 * Compacte variant voor de maan-transit modus (spec
 * `2026-07-06-hd-moon-transit-chart-design.md` §6.2): zelfde viewBox en
 * middelpunt, alle ring-radii ≈ ×0,84. Samen met de mode-SCSS op de
 * chart-mandala-stack (hoger hoogteplafond + bodygraph op 42%) omsluit het
 * wiel de verkleinde bodygraph volledig (finetune-ronde 2, 2026-07-06); de
 * rand-marge (viewBox 500 − 418) is de card-zone.
 *
 * Zelfde ringvolgorde als {@link DEFAULT_MANDALA_GEOMETRY}: de dierenriem is de
 * binnenste band, direct binnen de poortenring.
 */
export const MOON_TRANSIT_MANDALA_GEOMETRY: MandalaGeometryConfig = {
  center: { x: 500, y: 500 },
  viewBoxSize: 1000,
  quarterRing: { innerRadius: 382, outerRadius: 418 },
  ichingRing: { innerRadius: 349, outerRadius: 375 },
  gateRing: { innerRadius: 309, outerRadius: 344 },
  lineRing: { innerRadius: 334, outerRadius: 344 },
  // Binnenbanden (kalibratie voor buildMandalaWheelLayout): godhead direct
  // binnen de poortenring, de dierenriem daaronder — beide 26 breed, gap 6.
  godheadRing: { innerRadius: 277, outerRadius: 303 },
  zodiacRing: { innerRadius: 245, outerRadius: 271 },
};

/** Zichtbaarheid van de optionele mandala-ringen (shortcut-bar toggles). */
export interface MandalaRingVisibility {
  readonly zodiacRing: boolean;
  readonly quarterRing: boolean;
  readonly ichingRing: boolean;
  readonly godheadRing: boolean;
}

/**
 * De vier informatieve mandala-ringen voor de één-ring-modus op telefoon
 * (docs/MOBIEL.md §9, besluit 2): een toolbar-knop bladert er één voor één
 * doorheen zodat maar één infoband tegelijk ruimte inneemt.
 */
export type MandalaInfoRingKey = 'quarters' | 'godheads' | 'zodiac' | 'iching';

/**
 * Aanbevolen bladervolgorde voor de cycle-knop op telefoon: dierenriem →
 * kwartalen → godheads → I-Tjing. De `null`-stap (geen infoband) hoort vóór
 * de eerste ring en beheert de page zelf — die is geen ring.
 */
export const MANDALA_INFO_RING_CYCLE: readonly MandalaInfoRingKey[] = ['zodiac', 'quarters', 'godheads', 'iching'];

/**
 * Kleurvariant van de kwartaalband: zacht (A, default), voller/verzadigder
 * (B), of licht (C — de volheid van B maar richting wit gelicht i.p.v. de
 * grauwere papier-meng van A).
 */
export type HdMandalaQuarterColorVariant = 'soft' | 'vivid' | 'light';

/**
 * Kleurvariant van de dierenriem-band (feedback 2026-08-23): donker (default —
 * dusk-slate band met witte glyphs) of licht (bleke slate-tint met donkere
 * glyphs). Kleuren via `--mandala-zodiac-band*` in `_variables.scss`.
 */
export type HdMandalaZodiacRingVariant = 'dark' | 'light';

/**
 * Buitenrand van de kernmandala (de poortenring) én het harde wielplafond in
 * de transit-modi. {@link buildMandalaWheelLayout} legt de poortenring altijd
 * op deze straal; geven de transit-modi hem als `maxOuterRadius` mee, dan
 * schaalt het geheel zodat óók de optionele buitenringen binnen deze rand
 * blijven — de card-zone en de today-marker beginnen dan onafhankelijk van de
 * ring-toggles op dezelfde plek.
 */
export const MOON_TRANSIT_WHEEL_EDGE_RADIUS = MOON_TRANSIT_MANDALA_GEOMETRY.quarterRing.outerRadius;

/** Anker-straal van de formatie-cards: net buiten de wielrand (card-zone). */
export const MOON_TRANSIT_CARD_ANCHOR_RADIUS = MOON_TRANSIT_WHEEL_EDGE_RADIUS + 12;

/**
 * Afstand van het today-maanicoon tot de zichtbare wielrand (viewBox-units).
 * Gedeeld tussen het wiel (render) en de card-laag (phantom-obstakel in de
 * collision-detectie zodat cards het icoon nooit bedekken).
 */
export const MOON_TRANSIT_TODAY_ICON_EDGE_OFFSET = 19;

/**
 * Binnenstraal van de activatie-zone ("pizza slices") als fractie van de
 * binnenrand van de poortenring. In deze zone — tussen het binnengat van het
 * wiel en deze straal — worden per poort de slice-spaken getekend waarin een
 * volgende update de planeetactivatie-glyphs (design/personality/transit/
 * composite-kleuren) naar binnen toe stapelt. Een fractie i.p.v. een absolute
 * straal: de zone schaalt automatisch mee met de uniforme wiel-schaal van
 * {@link buildMandalaWheelLayout}.
 */
export const MANDALA_GATE_SLICE_INNER_RADIUS_FACTOR = 0.65;

/** Resultaat van {@link buildMandalaWheelLayout}: geometrie + toegepaste schaal. */
export interface MandalaWheelLayout {
  readonly geometry: MandalaGeometryConfig;
  /**
   * Uniforme schaal die op álle radii is toegepast: de buitenste zichtbare
   * ring landt altijd exact op `maxOuterRadius`. Groter dan 1 wanneer
   * verborgen buitenringen ruimte vrijgeven (het geheel groeit dan mee),
   * kleiner dan 1 wanneer de ringen anders buiten de rand zouden vallen. De
   * chart-laag op de pagina schaalt met dezelfde factor, zodat de verhouding
   * wiel ↔ chart altijd constant is.
   */
  readonly scale: number;
}

/**
 * Doel-buitenrand van het wiel in de gewone weergaven: de rand waarop de
 * buitenste zichtbare ring landt (= de buitenrand van de kwartaalring wanneer
 * álle ringen aan staan — de door de gebruiker goedgekeurde maximale maat).
 * Ringen verbergen geeft de vrijgekomen ruimte dus aan het geheel terug
 * i.p.v. een lege band achter te laten. Transit-modi geven in plaats hiervan
 * {@link MOON_TRANSIT_WHEEL_EDGE_RADIUS} mee zodat de card-zone vrij blijft.
 */
export const MANDALA_WHEEL_TARGET_OUTER_RADIUS =
  MOON_TRANSIT_WHEEL_EDGE_RADIUS
  + (MOON_TRANSIT_MANDALA_GEOMETRY.ichingRing.innerRadius - MOON_TRANSIT_MANDALA_GEOMETRY.gateRing.outerRadius)
  + (MOON_TRANSIT_MANDALA_GEOMETRY.ichingRing.outerRadius - MOON_TRANSIT_MANDALA_GEOMETRY.ichingRing.innerRadius)
  + (MOON_TRANSIT_MANDALA_GEOMETRY.quarterRing.innerRadius - MOON_TRANSIT_MANDALA_GEOMETRY.ichingRing.outerRadius)
  + (MOON_TRANSIT_MANDALA_GEOMETRY.quarterRing.outerRadius - MOON_TRANSIT_MANDALA_GEOMETRY.quarterRing.innerRadius);

/**
 * Bouwt de wiel-geometrie volgens het "naar buiten groeien"-model (2026-08-20):
 * de kernmandala (poorten- + lijnenring, met de dierenriem als binnenband)
 * heeft vaste basisverhoudingen — de poortenring eindigt in de basis op
 * {@link MOON_TRANSIT_WHEEL_EDGE_RADIUS} — en de optionele buitenringen
 * (I-Tjing, kwartaal) komen erbij áán de buitenkant, met de bandbreedtes en
 * tussenruimtes uit {@link MOON_TRANSIT_MANDALA_GEOMETRY}.
 *
 * Daarna wordt het geheel uniform geschaald zodat de buitenste zichtbare ring
 * exact op `maxOuterRadius` landt (default:
 * {@link MANDALA_WHEEL_TARGET_OUTER_RADIUS}; transit-modi geven de wielrand
 * mee zodat de card-zone erbuiten vrij blijft). Ringen verbergen geeft de
 * vrijgekomen ruimte dus aan het geheel terug (schaal > 1, alles groeit —
 * incl. de chart via {@link MandalaWheelLayout.scale}); ringen tonen laat de
 * kern op zijn basismaat (schaal 1) of krimpt alles samen binnen een krapper
 * plafond (schaal < 1). De onderlinge verhoudingen zijn altijd identiek.
 *
 * Verborgen ringen krijgen een nul-brede band op hun cursorpositie (worden
 * nooit gerenderd) en laten géén lege band achter. De dierenriem pakt als
 * binnenband naar binnen toe (2026-08-16) en telt niet mee voor de buitenrand.
 */
export function buildMandalaWheelLayout(
  visible: MandalaRingVisibility,
  options?: {
    readonly maxOuterRadius?: number;
    /**
     * Eén-info-ring-modus (telefoon, docs/MOBIEL.md §9 besluit 2): negeert
     * `visible` en toont van de vier infobanden enkel de gekozen — die
     * verhuist naar de buitenrand (ook de dierenriem/godheads, die normaal
     * binnenin liggen), de kern groeit op naar de vrijgekomen ruimte.
     * `null`/weggelaten = de gewone layout hierboven.
     */
    readonly singleInfoRing?: MandalaInfoRingKey | null;
  },
): MandalaWheelLayout {
  const maxOuterRadius = options?.maxOuterRadius ?? MANDALA_WHEEL_TARGET_OUTER_RADIUS;
  const singleInfoRing = options?.singleInfoRing ?? null;
  if (singleInfoRing != null) {
    return buildSingleInfoRingWheelLayout(singleInfoRing, maxOuterRadius);
  }
  const base = MOON_TRANSIT_MANDALA_GEOMETRY;
  const widthOf = (ring: MandalaRingRadii): number => ring.outerRadius - ring.innerRadius;
  // Kalibratie uit de basis-config: tussenruimte tussen elke ring en zijn buur.
  const gaps = {
    iching: base.ichingRing.innerRadius - base.gateRing.outerRadius,
    quarter: base.quarterRing.innerRadius - base.ichingRing.outerRadius,
    godhead: base.gateRing.innerRadius - base.godheadRing.outerRadius,
    zodiac: base.godheadRing.innerRadius - base.zodiacRing.outerRadius,
  };

  // Vaste kern: poorten- en lijnenring eindigen op de wielrand.
  const coreEdge = MOON_TRANSIT_WHEEL_EDGE_RADIUS;
  const gateRing = { innerRadius: coreEdge - widthOf(base.gateRing), outerRadius: coreEdge };
  const lineRing = { innerRadius: coreEdge - widthOf(base.lineRing), outerRadius: coreEdge };

  // Binnenbanden pakken vanaf de poortenring naar binnen toe aaneen: eerst de
  // godhead-band (binnen de Ring Pass Not, direct onder de poortenring), dan
  // de dierenriem. Verborgen banden laten geen lege ruimte achter en de
  // buitenringen worden hier nooit door beïnvloed.
  let innerCursor = gateRing.innerRadius;
  const packInnerRing = (ring: MandalaRingRadii, isVisible: boolean, gap: number): MandalaRingRadii => {
    if (!isVisible) {
      return { innerRadius: innerCursor, outerRadius: innerCursor };
    }
    const outerRadius = innerCursor - gap;
    const innerRadius = outerRadius - widthOf(ring);
    innerCursor = innerRadius;
    return { innerRadius, outerRadius };
  };
  const godheadRing = packInnerRing(base.godheadRing, visible.godheadRing, gaps.godhead);
  const zodiacRing = packInnerRing(base.zodiacRing, visible.zodiacRing, gaps.zodiac);

  // Buitenringen groeien vanaf de kernrand naar buiten toe aaneen.
  let cursor = coreEdge;
  const growRing = (ring: MandalaRingRadii, isVisible: boolean, gap: number): MandalaRingRadii => {
    if (!isVisible) {
      return { innerRadius: cursor, outerRadius: cursor };
    }
    const innerRadius = cursor + gap;
    const outerRadius = innerRadius + widthOf(ring);
    cursor = outerRadius;
    return { innerRadius, outerRadius };
  };
  const ichingRing = growRing(base.ichingRing, visible.ichingRing, gaps.iching);
  const quarterRing = growRing(base.quarterRing, visible.quarterRing, gaps.quarter);

  const scale = maxOuterRadius / cursor;
  const scaleRing = (ring: MandalaRingRadii): MandalaRingRadii => ({
    innerRadius: ring.innerRadius * scale,
    outerRadius: ring.outerRadius * scale,
  });

  return {
    scale,
    geometry: {
      center: base.center,
      viewBoxSize: base.viewBoxSize,
      quarterRing: scaleRing(quarterRing),
      ichingRing: scaleRing(ichingRing),
      gateRing: scaleRing(gateRing),
      lineRing: scaleRing(lineRing),
      godheadRing: scaleRing(godheadRing),
      zodiacRing: scaleRing(zodiacRing),
    },
  };
}

/**
 * Layout-variant voor de één-info-ring-modus (telefoon): zelfde "naar buiten
 * groeien"-model, maar met precies één infoband — gemonteerd áán de
 * buitenkant van de kern (óók de banden die normaal binnenin liggen), met de
 * gekalibreerde bandbreedte en tussenruimte uit
 * {@link MOON_TRANSIT_MANDALA_GEOMETRY} (binnenbanden hergebruiken hun
 * binnen-gap als buiten-gap). Daarna schaalt het geheel uniform zodat de
 * infoband exact op `maxOuterRadius` landt: de kern (poorten, lijnen,
 * activatie-zone) krijgt zo álle ruimte die de verborgen ringen vrijgeven en
 * de infoband groeit enkel met dezelfde bescheiden factor mee
 * (leesbaarheid). Niet-gekozen infobanden krijgen — net als verborgen ringen
 * in de gewone layout — een nul-brede band die nooit gerenderd wordt.
 */
function buildSingleInfoRingWheelLayout(
  singleInfoRing: MandalaInfoRingKey,
  maxOuterRadius: number,
): MandalaWheelLayout {
  const base = MOON_TRANSIT_MANDALA_GEOMETRY;
  const widthOf = (ring: MandalaRingRadii): number => ring.outerRadius - ring.innerRadius;
  // Kalibratie per infoband: eigen breedte + tussenruimte tegen de buurring.
  const bands: Readonly<Record<MandalaInfoRingKey, { readonly width: number; readonly gap: number }>> = {
    quarters: { width: widthOf(base.quarterRing), gap: base.quarterRing.innerRadius - base.ichingRing.outerRadius },
    iching: { width: widthOf(base.ichingRing), gap: base.ichingRing.innerRadius - base.gateRing.outerRadius },
    godheads: { width: widthOf(base.godheadRing), gap: base.gateRing.innerRadius - base.godheadRing.outerRadius },
    zodiac: { width: widthOf(base.zodiacRing), gap: base.godheadRing.innerRadius - base.zodiacRing.outerRadius },
  };

  // Vaste kern zoals in de gewone layout: poorten- en lijnenring op de wielrand.
  const coreEdge = MOON_TRANSIT_WHEEL_EDGE_RADIUS;
  const gateRing = { innerRadius: coreEdge - widthOf(base.gateRing), outerRadius: coreEdge };
  const lineRing = { innerRadius: coreEdge - widthOf(base.lineRing), outerRadius: coreEdge };

  // De gekozen infoband direct buiten de kern; alles schaalt daarna zó dat
  // zijn buitenrand exact op maxOuterRadius landt.
  const band = bands[singleInfoRing];
  const infoRing: MandalaRingRadii = {
    innerRadius: coreEdge + band.gap,
    outerRadius: coreEdge + band.gap + band.width,
  };
  const scale = maxOuterRadius / infoRing.outerRadius;
  const scaleRing = (ring: MandalaRingRadii): MandalaRingRadii => ({
    innerRadius: ring.innerRadius * scale,
    outerRadius: ring.outerRadius * scale,
  });
  // Nul-brede band voor elke niet-gekozen infoband: buitenbanden op de
  // kernrand, binnenbanden op de poortenring-binnenrand (zelfde conventie als
  // verborgen ringen in de gewone layout).
  const infoOrZero = (key: MandalaInfoRingKey, hiddenRadius: number): MandalaRingRadii =>
    key === singleInfoRing ? infoRing : { innerRadius: hiddenRadius, outerRadius: hiddenRadius };

  return {
    scale,
    geometry: {
      center: base.center,
      viewBoxSize: base.viewBoxSize,
      quarterRing: scaleRing(infoOrZero('quarters', coreEdge)),
      ichingRing: scaleRing(infoOrZero('iching', coreEdge)),
      gateRing: scaleRing(gateRing),
      lineRing: scaleRing(lineRing),
      godheadRing: scaleRing(infoOrZero('godheads', gateRing.innerRadius)),
      zodiacRing: scaleRing(infoOrZero('zodiac', gateRing.innerRadius)),
    },
  };
}
